import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Container,
  FormControl,
  Link,
  Paper,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Button from "@mui/material/Button";
import { pick } from "underscore";
import {
  clearGlobalStorage,
  readFromGlobalStorage,
  writeToGlobalStorage,
} from "../userStorage/globalUserStorage";
import { UserStorageKey } from "../userStorage/userStorageKey";
import { SOLVER_OPTIONS } from "fe-common/featureFlags/getFeatureFlags";
import { RoleRestrictor } from "../roles/RoleRestrictor";
import { downloadObjectAsJson } from "./util";
import { SolverFeatureFlagsGroup } from "./SolverFeatureFlagsGroup";
import { FeatureFlagsGroup } from "./FeatureFlagsGroup";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { WebDao } from "fe-common/graphql/dao";
import { gqlClient } from "fe-common/graphql/webClient";
import { GraphQlEnvPicker } from "./graphQlEnvPicker";
import { KEY_GRAPHQL_SETTINGS } from "./common/KEY_GRAPHQL_SETTINGS";
import {
  fromLocalStorage,
  useStateWithLocalStorage,
} from "./common/localStorage";
// import { UserRoles } from "fe-common/graphql";

export enum RunningEnvironment {
  DEVELOP = "develop",
  PRODUCTION = "prod",
  JDEVLATEST = "jdevlatest",
  CUSTOM = "custom",
}
export enum RulesCommit {
  latest = "latest",
  moderated = "moderated",
}

export interface GraphqlClientSettings {
  runningEnv?: RunningEnvironment;
  rules?: RulesCommit;
  endpoint?: string;
  editorRunningEnv?: RunningEnvironment;
  editorEndpoint?: string;
  preloaderEndPoint?: string;
}

const FormContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const SettingsBlock = styled(Paper)({
  margin: "15px",
  padding: "30px",
  width: "90%",
});

const KEY_FEATURES_SETTINGS = "kahun.features-settings";

export const useGraphQlClientSettings = (): GraphqlClientSettings =>
  fromLocalStorage(KEY_GRAPHQL_SETTINGS);

const featureFlags = require("../../fe-common/featureFlags/featureFlags.json");
const featuresMap = pick(
  featureFlags[process.env.REACT_APP_ENVIRONMENT],
  (value) => typeof value === "boolean",
);

type SolverFlagsGroup = {
  name: string;
  flags: {
    name: string;
    defaultValue: string;
    type: {
      name: string;
    };
  }[];
};

//const dao = new WebDao(() => gqlClient());
const dao = new WebDao(gqlClient);

export const GraphqlClientSettingsPage = () => {
  const [settings, setSettings] = useStateWithLocalStorage(
    KEY_GRAPHQL_SETTINGS,
  );

  const [features, setFeatures] = useState(featuresMap);

  const [defaults, setDefaults] = useState({});

  const fetchSolverFlags = useCallback(async () => {
    let flags: SolverFlagsGroup[] = [];
    try {
      const promises = SOLVER_OPTIONS.map((o) => dao.getOptions(o));
      const ret = await Promise.all(promises);
      flags = SOLVER_OPTIONS.map((item, index) => ({
        name: item,
        flags: ret[index],
      }));
    } catch (e) {
      console.log("Couldn't fetch solver flags.");
    }

    const solverFeatures = {};
    flags.forEach((fg) =>
      fg.flags.forEach((f) => {
        try {
          if (f.type.name === "Boolean") {
            //To handle nulls
            solverFeatures[`${fg.name}_${f.name}`] = f.defaultValue === "true";
          } else {
            solverFeatures[`${fg.name}_${f.name}`] = JSON.parse(f.defaultValue);
          }
        } catch (e) {
          if (isString(f?.defaultValue)) {
            // a non-json string.
            solverFeatures[`${fg.name}_${f.name}`] = f.defaultValue;
          }
        }
      }),
    );

    return solverFeatures;
  }, []);

  useEffect(() => {
    (async () => {
      const solverFeatures = await fetchSolverFlags();
      const localFeatures =
        JSON.parse(localStorage.getItem(KEY_FEATURES_SETTINGS)) || {};
      setDefaults(solverFeatures);
      localFeatures &&
        setFeatures({ ...features, ...solverFeatures, ...localFeatures });
    })();
  }, [dao]);

  const handleChange = (field: keyof GraphqlClientSettings) => (event: any) => {
    const v = event?.target?.value;
    if (v != undefined) setSettings((s) => ({ ...s, [field]: v }));
  };

  const isString = (val) =>
    val && (typeof val == "string" || val instanceof String);

  const handleTestReload = () => {
    writeToGlobalStorage(UserStorageKey.ReloadNeeded, "true");
    clearGlobalStorage(UserStorageKey.PreventStartOver);
  };
  const handlePWARemoveDismissed = () => {
    clearGlobalStorage(UserStorageKey.InstallPwa);
  };

  const handleFeaturesChange = (evertTarget) => (event) => {
    setFeatures({
      ...features,
      [event.target.name]: event.target[evertTarget],
    });
    const localFeatures: any = readFromGlobalStorage(
      UserStorageKey.FeatureFlags,
    );
    writeToGlobalStorage(UserStorageKey.FeatureFlags, {
      ...localFeatures,
      [event.target.name]: event.target[evertTarget],
    });
  };

  const clearLocalFeatures = () => {
    (async () => {
      const solverFeatures = await fetchSolverFlags();
      clearGlobalStorage(UserStorageKey.FeatureFlags);
      setFeatures({ ...featuresMap, ...solverFeatures });
    })();
  };

  const clientFlags = Object.keys(features).filter((f) =>
    SOLVER_OPTIONS.every((so) => !f.includes(so)),
  );

  return (
    // <RoleRestrictor
    //   roles={[UserRoles.KahunBackofficeUsers]}
    //   redirect
    //   showLoading
    // >
    <Container
      maxWidth="md"
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <SettingsBlock>
        <GraphQlEnvPicker />
      </SettingsBlock>
      <SettingsBlock>
        <Typography color="primary" variant="h4">
          Editor (Java) backend
        </Typography>
        <FormContainer>
          <FormControl component="fieldset">
            <FormLabel component="legend">Backend env:</FormLabel>
            <RadioGroup
              name="env"
              value={settings.editorRunningEnv}
              onChange={handleChange("editorRunningEnv")}
            >
              <FormControlLabel
                value={RunningEnvironment.PRODUCTION}
                control={<Radio />}
                label="prod"
              />
              <FormControlLabel
                value={RunningEnvironment.DEVELOP}
                control={<Radio />}
                label="develop"
              />
              <FormControlLabel
                value={RunningEnvironment.CUSTOM}
                control={<Radio />}
                label="custom"
              />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" style={{ marginTop: 14 }}>
            <FormLabel component="legend">Endpoint Override:</FormLabel>
            <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
              <TextField
                label="Host URL"
                placeholder={`http://localhost:5012/med`}
                value={settings.editorEndpoint || ""}
                onChange={handleChange("editorEndpoint")}
                disabled={
                  settings.editorRunningEnv !== RunningEnvironment.CUSTOM
                }
                style={{ width: 240 }}
              />
              {settings.editorRunningEnv === RunningEnvironment.CUSTOM && (
                <>
                  {settings.editorEndpoint && (
                    <>
                      <Link
                        role="button"
                        onClick={() =>
                          setSettings((s) => ({
                            ...s,
                            editorEndpoint: "",
                          }))
                        }
                      >
                        clear
                      </Link>
                      |
                    </>
                  )}
                  <Link
                    role="button"
                    onClick={() =>
                      setSettings((s) => ({
                        ...s,
                        editorRunningEnv: RunningEnvironment.CUSTOM,
                        editorEndpoint: "http://localhost:5012/med",
                      }))
                    }
                  >
                    localhost
                  </Link>
                </>
              )}
            </div>
          </FormControl>
        </FormContainer>
      </SettingsBlock>
      <SettingsBlock>
        <Typography color="primary" variant="h4">
          General
        </Typography>
        <Button onClick={handleTestReload}>Test PWA install-reload</Button>
        <Button onClick={handlePWARemoveDismissed}>
          Clear PWA dismissed data
        </Button>
      </SettingsBlock>
      <SettingsBlock>
        <Button
          variant="contained"
          color="primary"
          style={{ marginRight: "5px" }}
          onClick={() =>
            downloadObjectAsJson(
              JSON.stringify({ settings, features }),
              "featureFlags",
            )
          }
        >
          Download
        </Button>
        <label
          style={{
            backgroundColor: "#3f51b5",
            marginRight: "5px",
            color: "white",
          }}
        >
          Upload:
        </label>
        <input
          type="file"
          id="Upload"
          accept={"text/json"}
          onChange={(e) => {
            const reader = new FileReader();
            reader.addEventListener("load", (event) => {
              const f: any = event.target.result;
              const data = JSON.parse(f);
              console.log(data);
              setSettings(data.settings);
              setFeatures(data.features);
            });
            reader.readAsText(e.target.files[0]);
          }}
        />
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography color="primary" variant="h4">
              Solver Feature Flags
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SolverFeatureFlagsGroup
              features={features}
              handleFeaturesChange={handleFeaturesChange}
              defaults={defaults || {}}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography color="primary" variant="h4">
              Client Feature Flags
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FeatureFlagsGroup
              keys={clientFlags}
              features={features}
              handleFeaturesChange={handleFeaturesChange}
              defaults={featuresMap}
            />
          </AccordionDetails>
        </Accordion>
        <Button
          variant="contained"
          color="primary"
          onClick={clearLocalFeatures}
        >
          Reset default
        </Button>
      </SettingsBlock>
    </Container>
    // </RoleRestrictor>
  );
};
